// MedicalTermManager.js
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Modal, Form,
  Alert, Spinner, Badge, InputGroup, Pagination, Tabs, Tab
} from 'react-bootstrap';
import apiClient from '../../apiClient';
import './MedicalTermManager.css';

const MedicalTermManager = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [stats, setStats] = useState({});

  // Form state
  const [newTerm, setNewTerm] = useState({
    incorrect: '',
    correct: '',
    category: 'other',
    profession: '',
    clinic_specific: false
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    profession: '',
    page: 1,
    per_page: 20
  });

  // Pagination
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchTerms();
    fetchCategories();
    fetchProfessions();
    fetchStats();
  }, [filters]);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await apiClient.get(`/api/medical-terms?${params}`);
      setTerms(response.data.terms);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError('Kunde inte hämta medicinska termer');
      console.error('Error fetching terms:', err);
      // Sätt dummy data så att UI kan visas ändå
      setTerms([]);
      setPagination({ pages: 1, page: 1, has_prev: false, has_next: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/api/medical-terms/categories');
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Sätt fallback kategorier
      setCategories([
        { value: 'medication', label: 'Medicin' },
        { value: 'diagnosis', label: 'Diagnos' },
        { value: 'test', label: 'Test' },
        { value: 'anatomy', label: 'Anatomi' },
        { value: 'other', label: 'Övrigt' }
      ]);
    }
  };

  const fetchProfessions = async () => {
    try {
      const response = await apiClient.get('/api/medical-terms/professions');
      setProfessions(response.data.professions);
    } catch (err) {
      console.error('Error fetching professions:', err);
      // Sätt fallback yrkesgrupper
      setProfessions([
        { value: 'physiotherapist', label: 'Fysioterapeut' },
        { value: 'psychologist', label: 'Psykolog' },
        { value: 'doctor', label: 'Läkare' },
        { value: 'nurse', label: 'Sjuksköterska' }
      ]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/api/medical-terms/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Sätt fallback stats
      setStats({
        total_terms: 0,
        clinic_terms: 0,
        global_terms: 0,
        category_breakdown: {}
      });
    }
  };

  const handleAddTerm = async () => {
    try {
      await apiClient.post('/api/medical-terms', newTerm);
      setShowAddModal(false);
      setNewTerm({
        incorrect: '',
        correct: '',
        category: 'other',
        profession: '',
        clinic_specific: false
      });
      fetchTerms();
      fetchStats();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte lägga till term');
    }
  };

  const handleUpdateTerm = async () => {
    try {
      await apiClient.put(`/api/medical-terms/${editingTerm.id}`, editingTerm);
      setEditingTerm(null);
      fetchTerms();
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte uppdatera term');
    }
  };

  const handleDeleteTerm = async (termId) => {
    if (window.confirm('Är du säker på att du vill ta bort denna term?')) {
      try {
        await apiClient.delete(`/api/medical-terms/${termId}`);
        fetchTerms();
        fetchStats();
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Kunde inte ta bort term');
      }
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post('/api/medical-terms/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(response.data.message);
      if (response.data.errors.length > 0) {
        console.warn('Import errors:', response.data.errors);
      }
      
      setShowImportModal(false);
      fetchTerms();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.error || 'Import misslyckades');
    }
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const renderStatsCard = () => (
    <Card className="mb-4">
      <Card.Header>
        <h5>Statistik</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={3}>
            <div className="stat-item">
              <h4 className="text-primary">{stats.total_terms || 0}</h4>
              <small className="text-muted">Totalt antal termer</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-item">
              <h4 className="text-success">{stats.clinic_terms || 0}</h4>
              <small className="text-muted">Klinikspecifika</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-item">
              <h4 className="text-info">{stats.global_terms || 0}</h4>
              <small className="text-muted">Globala termer</small>
            </div>
          </Col>
          <Col md={3}>
            <div className="stat-item">
              <h4 className="text-warning">
                {Object.keys(stats.category_breakdown || {}).length}
              </h4>
              <small className="text-muted">Kategorier</small>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderFilters = () => (
    <Card className="mb-4">
      <Card.Body>
        <Row>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Sök</Form.Label>
              <Form.Control
                type="text"
                placeholder="Sök i termer..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Kategori</Form.Label>
              <Form.Select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value, page: 1 }))}
              >
                <option value="">Alla kategorier</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Yrkesgrupp</Form.Label>
              <Form.Select
                value={filters.profession}
                onChange={(e) => setFilters(prev => ({ ...prev, profession: e.target.value, page: 1 }))}
              >
                <option value="">Alla yrkesgrupper</option>
                {professions.map(prof => (
                  <option key={prof.value} value={prof.value}>{prof.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderTermsTable = () => {
    return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5>Medicinska termer</h5>
        <div>
          <button 
            onClick={() => setShowImportModal(true)} 
            style={{
              display: 'inline-block',
              visibility: 'visible',
              opacity: 1,
              backgroundColor: 'transparent',
              color: '#007bff',
              border: '1px solid #007bff',
              padding: '0.375rem 0.75rem',
              borderRadius: '0.375rem',
              marginRight: '0.5rem',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <i className="fas fa-upload"></i> Importera CSV
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'inline-block',
              visibility: 'visible',
              opacity: 1,
              backgroundColor: '#007bff',
              color: '#ffffff',
              border: '1px solid #007bff',
              padding: '0.375rem 0.75rem',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <i className="fas fa-plus"></i> Lägg till term
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Felstavning</th>
                  <th>Korrekt stavning</th>
                  <th>Kategori</th>
                  <th>Yrkesgrupp</th>
                  <th>Användningar</th>
                  <th>Typ</th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {terms.map(term => (
                  <tr key={term.id}>
                    <td>
                      {editingTerm?.id === term.id ? (
                        <Form.Control
                          size="sm"
                          value={editingTerm.incorrect}
                          onChange={(e) => setEditingTerm(prev => ({ ...prev, incorrect: e.target.value }))}
                        />
                      ) : (
                        <code>{term.incorrect}</code>
                      )}
                    </td>
                    <td>
                      {editingTerm?.id === term.id ? (
                        <Form.Control
                          size="sm"
                          value={editingTerm.correct}
                          onChange={(e) => setEditingTerm(prev => ({ ...prev, correct: e.target.value }))}
                        />
                      ) : (
                        <strong>{term.correct}</strong>
                      )}
                    </td>
                    <td>
                      {editingTerm?.id === term.id ? (
                        <Form.Select
                          size="sm"
                          value={editingTerm.category}
                          onChange={(e) => setEditingTerm(prev => ({ ...prev, category: e.target.value }))}
                        >
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        <Badge bg="secondary">
                          {categories.find(c => c.value === term.category)?.label || term.category}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {editingTerm?.id === term.id ? (
                        <Form.Select
                          size="sm"
                          value={editingTerm.profession || ''}
                          onChange={(e) => setEditingTerm(prev => ({ ...prev, profession: e.target.value || null }))}
                        >
                          <option value="">Alla</option>
                          {professions.map(prof => (
                            <option key={prof.value} value={prof.value}>{prof.label}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        term.profession ? (
                          <Badge bg="info">
                            {professions.find(p => p.value === term.profession)?.label || term.profession}
                          </Badge>
                        ) : (
                          <span className="text-muted">Alla</span>
                        )
                      )}
                    </td>
                    <td>
                      <Badge bg="warning">{term.usage_count}</Badge>
                    </td>
                    <td>
                      <Badge bg={term.is_global ? "success" : "primary"}>
                        {term.is_global ? "Global" : "Klinik"}
                      </Badge>
                    </td>
                    <td>
                      {editingTerm?.id === term.id ? (
                        <>
                          <Button 
                            size="sm" 
                            variant="success" 
                            onClick={handleUpdateTerm} 
                            className="me-1"
                            style={{
                              display: 'inline-block',
                              visibility: 'visible',
                              opacity: 1,
                              backgroundColor: '#28a745',
                              color: '#ffffff',
                              border: '1px solid #28a745',
                              padding: '0.25rem 0.5rem'
                            }}
                          >
                            <i className="fas fa-check"></i>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            onClick={() => setEditingTerm(null)}
                            style={{
                              display: 'inline-block',
                              visibility: 'visible',
                              opacity: 1,
                              backgroundColor: '#6c757d',
                              color: '#ffffff',
                              border: '1px solid #6c757d',
                              padding: '0.25rem 0.5rem'
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline-primary" 
                            onClick={() => setEditingTerm(term)}
                            className="me-1"
                            style={{
                              display: 'inline-block',
                              visibility: 'visible',
                              opacity: 1,
                              backgroundColor: 'transparent',
                              color: '#007bff',
                              border: '1px solid #007bff',
                              padding: '0.25rem 0.5rem'
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline-danger" 
                            onClick={() => handleDeleteTerm(term.id)}
                            style={{
                              display: 'inline-block',
                              visibility: 'visible',
                              opacity: 1,
                              backgroundColor: 'transparent',
                              color: '#dc3545',
                              border: '1px solid #dc3545',
                              padding: '0.25rem 0.5rem'
                            }}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {pagination.pages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.Prev 
                    disabled={!pagination.has_prev}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  />
                  {[...Array(pagination.pages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === pagination.page}
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next 
                    disabled={!pagination.has_next}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
    );
  };

  return (
    <Container fluid className="medical-terms-container">
      <Row className="mb-4">
        <Col>
          <h2>Hantera medicinska termer</h2>
          <p className="text-muted">
            Administrera korrigeringar för medicinska termer som används vid transkribering.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {renderStatsCard()}
      {renderFilters()}
      {renderTermsTable()}

      {/* Add Term Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Lägg till medicinsk term</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Felstavning</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="t.ex. mc murray test"
                    value={newTerm.incorrect}
                    onChange={(e) => setNewTerm(prev => ({ ...prev, incorrect: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Korrekt stavning</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="t.ex. McMurrays test"
                    value={newTerm.correct}
                    onChange={(e) => setNewTerm(prev => ({ ...prev, correct: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kategori</Form.Label>
                  <Form.Select
                    value={newTerm.category}
                    onChange={(e) => setNewTerm(prev => ({ ...prev, category: e.target.value }))}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Yrkesgrupp (valfritt)</Form.Label>
                  <Form.Select
                    value={newTerm.profession}
                    onChange={(e) => setNewTerm(prev => ({ ...prev, profession: e.target.value }))}
                  >
                    <option value="">Alla yrkesgrupper</option>
                    {professions.map(prof => (
                      <option key={prof.value} value={prof.value}>{prof.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Check
              type="checkbox"
              label="Endast för min klinik"
              checked={newTerm.clinic_specific}
              onChange={(e) => setNewTerm(prev => ({ ...prev, clinic_specific: e.target.checked }))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowAddModal(false)}
            style={{
              display: 'inline-block',
              visibility: 'visible',
              opacity: 1,
              backgroundColor: '#6c757d',
              color: '#ffffff',
              border: '1px solid #6c757d',
              padding: '0.375rem 0.75rem'
            }}
          >
            Avbryt
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddTerm}
            style={{
              display: 'inline-block',
              visibility: 'visible',
              opacity: 1,
              backgroundColor: '#007bff',
              color: '#ffffff',
              border: '1px solid #007bff',
              padding: '0.375rem 0.75rem'
            }}
          >
            Lägg till
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Importera medicinska termer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Ladda upp en CSV-fil med följande kolumner:</p>
          <ul>
            <li><code>incorrect</code> - Felstavning</li>
            <li><code>correct</code> - Korrekt stavning</li>
            <li><code>category</code> - Kategori (medication, diagnosis, test, anatomy, other)</li>
            <li><code>profession</code> - Yrkesgrupp (valfritt)</li>
            <li><code>clinic_specific</code> - true/false för klinikspecifik</li>
          </ul>
          <Form.Control
            type="file"
            accept=".csv"
            onChange={handleImport}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowImportModal(false)}
            style={{
              display: 'inline-block',
              visibility: 'visible',
              opacity: 1,
              backgroundColor: '#6c757d',
              color: '#ffffff',
              border: '1px solid #6c757d',
              padding: '0.375rem 0.75rem'
            }}
          >
            Stäng
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MedicalTermManager;